import { useLocation } from '@remix-run/react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useCallback, useEffect, useState } from 'react';
import type { CapiProductPage, Raffle } from 'types/capi/pdp';

import { Button } from '~/components/Button';
import AffiliateLogo from '~/components/Pdp/WhereToBuyInStock/AffiliateLogo';
import useEnv from '~/hooks/useEnv';
import useMarketingCookies from '~/hooks/useMarketingCookies';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import wct from '~/services/wct.client';
import { formatRaffleDate } from '~/utils/dates';
import { getTrackedUrlAndClickRef } from '~/utils/tracking';

dayjs.extend(duration);

type RaffleListingProps = {
  buttonPosition: number;
  raffleListing: Raffle;
};

export type CountdownStateProps = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

type CountdownProps = {
  countdown: CountdownStateProps;
  raffleEnded: boolean;
  setCountdown: React.Dispatch<CountdownStateProps>;
  setRaffleEnded: React.Dispatch<boolean>;
  unixTimestamp: number;
};

function getTimeDifferenceFromNow(timestamp: number) {
  const startMs = Date.now();
  const endMs = timestamp * 1000;

  const differenceMs = Math.abs(endMs - startMs);

  const duration = dayjs.duration(differenceMs);

  return {
    days: duration.days(),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds()
  };
}

const Countdown = ({
  unixTimestamp,
  countdown,
  setCountdown,
  raffleEnded,
  setRaffleEnded
}: CountdownProps) => {
  const dateCalculation = useCallback(() => {
    if (Date.now() / 1000 > unixTimestamp) {
      setRaffleEnded(true);
    } else {
      setCountdown(getTimeDifferenceFromNow(unixTimestamp));
    }
  }, [unixTimestamp, setRaffleEnded, setCountdown]);

  useEffect(() => {
    dateCalculation();

    const interval = setInterval(() => {
      dateCalculation();
    }, 1000);

    return () => clearInterval(interval);
  }, [dateCalculation]);

  if (raffleEnded) return null;

  return (
    <div className="flex gap-1">
      <CountdownUnit unit="Day" value={countdown.days} />
      <CountdownUnitSeperator />
      <CountdownUnit unit="Hrs" value={countdown.hours} />
      <CountdownUnitSeperator />
      <CountdownUnit unit="Min" value={countdown.minutes} />
      <CountdownUnitSeperator />
      <CountdownUnit unit="Sec" value={countdown.seconds} />
    </div>
  );
};

function CountdownUnit({ unit, value }: { unit: string; value: number }) {
  return (
    <div className="flex flex-col">
      <span className="flex justify-center font-bold text-grey-read">
        {value}
      </span>
      <span className="rounded-sm bg-concrete px-0.5 text-[10px]">{unit}</span>
    </div>
  );
}

function CountdownUnitSeperator() {
  return <span className="font-bold text-grey-read">:</span>;
}

const RaffleListing = ({
  raffleListing,
  buttonPosition
}: RaffleListingProps) => {
  const [countdown, setCountdown] = useState<CountdownStateProps>({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  const [raffleEnded, setRaffleEnded] = useState(false);
  const user = useUser();
  const env = useEnv();
  const marketingCookies = useMarketingCookies();
  const product = usePageData<CapiProductPage>();
  const { clientBrowser, clientIp } = useRootData();
  const { pathname, search } = useLocation();

  const { url, clickRef } = getTrackedUrlAndClickRef(raffleListing, {
    buttonPosition,
    user
  });

  const onButtonClick = () => {
    const pagePath = `${env.SITE_BASE_URL}${pathname}${search}`;

    return wct.fireEvent({
      clickRef,
      clientBrowser,
      clientIp,
      landingPage: marketingCookies.getLandingPage() ?? 'unknown',
      pagePath,
      product,
      url
    });
  };

  return (
    <div className="bg-white">
      <div className="flex cursor-pointer flex-row justify-between gap-3 p-4">
        <div className="flex grow items-center gap-1">
          <div className="h-9 w-16">
            <AffiliateLogo
              src={raffleListing.affiliate.logo}
              alt={raffleListing.id}
            />
          </div>
          <div className="text-nowrap text-base">
            {raffleEnded ? (
              <p>Closed</p>
            ) : (
              <p className="text-forest">Open until</p>
            )}
            <span className="text-sm text-grey-hide">
              {formatRaffleDate(raffleListing.close)}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center">
          <Countdown
            countdown={countdown}
            setCountdown={setCountdown}
            raffleEnded={raffleEnded}
            setRaffleEnded={setRaffleEnded}
            unixTimestamp={raffleListing.close}
          />
        </div>
      </div>
      <div className="px-4 pb-6">
        <a
          data-cy="raffle-button"
          onClick={onButtonClick}
          href={url}
          target="_blank"
          rel="sponsored nofollow noopener"
        >
          <Button variant="forest">Enter Raffle</Button>
        </a>
      </div>
    </div>
  );
};

export default RaffleListing;
